/**
 * CreateWorkspaceStep - name + URL form rendered inside the
 * CreateWorkspaceModal (and reusable elsewhere).
 *
 * Submit calls `useOrg().createWorkspace(name, websiteUrl)` which inserts
 * into the legacy `brands` table (Phase A). When a URL is provided the step
 * fires the brand-research v2 pipeline non-blocking via
 * `kickoffBrandResearch` (see `lib/brand-research.ts`), which POSTs to the
 * offline-session worker's `/workspace/:id/research` endpoint. The worker
 * handles auth, persistence, and SSE progress; the modal dismisses
 * immediately. Failures inside the kickoff are captured to PostHog by the
 * library; this component only catches the unlikely case where session
 * resolution itself throws.
 *
 * Naming contract: DB column / table is `brands`; UI says "workspace".
 *
 * @module components/workspace/CreateWorkspaceStep
 */

import React, { FormEvent, useState } from 'react';
import { useOrg } from '../../lib/OrgProvider';
import { useSupabase } from '../../lib/SupabaseProvider';
import { kickoffBrandResearch } from '../../lib/brand-research';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import { ButtonContent } from '../ui/ButtonContent';

interface CreateWorkspaceStepProps {
  /** Optional pre-populated workspace name. */
  initialName?: string;
  /** Submit-button label override. Default `"Create"`. */
  submitLabel?: string;
  /** Called after the workspace row exists. Fires before the non-blocking
   *  research kickoff resolves. */
  onComplete?: () => void;
  /** When true, suppress the local heading. Set when a parent (e.g. a
   *  Modal) already supplies its own title. */
  hideHeading?: boolean;
}

/**
 * Two-input form that creates a workspace and kicks off optional brand
 * research in the background.
 */
export const CreateWorkspaceStep: React.FC<CreateWorkspaceStepProps> = ({
  initialName = '',
  submitLabel = 'Create',
  onComplete,
  hideHeading = false,
}) => {
  const { createWorkspace } = useOrg();
  const { supabase } = useSupabase();

  const [name, setName] = useState(initialName);
  const [url, setUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isValid = name.trim().length > 0;

  /**
   * Fire-and-forget brand-research kickoff. Resolves the user's session
   * for the Bearer token, then hands off to `kickoffBrandResearch` which
   * POSTs the worker and consumes its SSE stream into the shared label
   * tracker. Failures are captured to PostHog inside the library; this
   * wrapper only catches the unlikely case where session resolution
   * itself throws.
   *
   * @param trimmedUrl  Non-empty URL collected from the form.
   * @param workspaceId Newly-created workspace id.
   */
  const kickoffResearch = async (
    trimmedUrl: string,
    workspaceId: string,
  ): Promise<void> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      if (!accessToken) {
        captureException(
          new Error('No access token available for brand research kickoff'),
          'CreateWorkspaceStep.kickoffResearch',
          { workspaceId },
        );
        return;
      }
      await kickoffBrandResearch({
        workspaceId,
        url: trimmedUrl,
        accessToken,
      });
    } catch (err) {
      captureException(
        err instanceof Error ? err : new Error(String(err)),
        'CreateWorkspaceStep.kickoffResearch',
        { workspaceId },
      );
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    setError(null);
    setSubmitting(true);
    try {
      const created = await createWorkspace(name.trim(), url.trim());
      onComplete?.();

      const trimmed = url.trim();
      if (trimmed.length > 0) {
        // Non-blocking: the modal already dismissed via onComplete.
        void kickoffResearch(trimmed, created.id);
      }
    } catch {
      setError("Couldn't create workspace. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 flex-1 flex flex-col">
      {!hideHeading && (
        <div className="mb-4">
          <h2 className="text-xl font-bold text-foreground">Name your workspace</h2>
          <p className="text-muted-foreground text-sm mt-1">
            Group your conversations, brand context, and assets in one place.
          </p>
        </div>
      )}

      <div>
        <label
          htmlFor="workspace-name"
          className="block text-sm font-medium text-muted-foreground mb-1.5"
        >
          Workspace name
        </label>
        <input
          id="workspace-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Acme Marketing"
          autoFocus
          className="w-full px-4 py-2.5 bg-surface border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:border-volt"
        />
      </div>

      <div>
        <label
          htmlFor="workspace-url"
          className="block text-sm font-medium text-muted-foreground mb-1.5"
        >
          Website
        </label>
        <input
          id="workspace-url"
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://acme.com"
          className="w-full px-4 py-2.5 bg-surface border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:border-volt"
        />
        <p className="text-xs text-muted-foreground mt-1">
          We'll use this to draft brand context. Optional.
        </p>
      </div>

      {error && <p className="text-danger text-sm">{error}</p>}

      <div className="flex-1" />
      <button
        type="submit"
        disabled={!isValid || submitting}
        aria-busy={submitting}
        className="w-full rounded-lg px-4 py-2.5 button-accent font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ButtonContent loading={submitting}>{submitLabel}</ButtonContent>
      </button>
    </form>
  );
};

export default CreateWorkspaceStep;
